import { useEffect, useState } from "react";
import PortalLayout from "@/layouts/PortalLayout";
import { api } from "@/lib/api";
import { History, RefreshCw, Filter, User } from "lucide-react";

const ACTION_COLOR = {
  create: "bg-emerald-100 text-emerald-700",
  update: "bg-blue-100 text-blue-700",
  delete: "bg-red-100 text-red-700",
  archive: "bg-amber-100 text-amber-700",
  unarchive: "bg-amber-100 text-amber-700",
  merge: "bg-purple-100 text-purple-700",
  convert: "bg-wine-100 text-wine-700",
};

export default function AdminActivity() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState({ action: "", entity_type: "", actor_email: "" });

  const load = () => {
    const params = {};
    if (filter.action) params.action = filter.action;
    if (filter.entity_type) params.entity_type = filter.entity_type;
    if (filter.actor_email) params.actor_email = filter.actor_email;
    api.get("/admin/activity", { params }).then((r) => setRows(r.data)).catch(() => {});
    api.get("/admin/activity/stats").then((r) => setStats(r.data)).catch(() => {});
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filter.action, filter.entity_type, filter.actor_email]);

  return (
    <PortalLayout sidebar="admin">
      <div className="space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-serif text-4xl text-navy">Activity Log</h1>
            <p className="mt-1 text-sm text-neutral-600">Journal d&apos;audit des actions admin · {stats?.total ?? "—"} événements au total.</p>
          </div>
          <button onClick={load} className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-bold text-neutral-700 hover:bg-neutral-50" data-testid="refresh-activity">
            <RefreshCw className="h-3.5 w-3.5" /> Rafraîchir
          </button>
        </div>

        <div className="rounded-2xl border border-neutral-100 bg-white p-4">
          <div className="flex flex-wrap items-center gap-3">
            <Filter className="h-4 w-4 text-neutral-400" />
            <select value={filter.action} onChange={(e) => setFilter({ ...filter, action: e.target.value })} className="rounded-lg border border-neutral-200 px-3 py-1.5 text-sm" data-testid="filter-action">
              <option value="">Toutes actions</option>
              <option value="create">Create</option>
              <option value="update">Update</option>
              <option value="delete">Delete</option>
              <option value="archive">Archive</option>
              <option value="merge">Merge</option>
              <option value="convert">Convert</option>
            </select>
            <select value={filter.entity_type} onChange={(e) => setFilter({ ...filter, entity_type: e.target.value })} className="rounded-lg border border-neutral-200 px-3 py-1.5 text-sm" data-testid="filter-entity">
              <option value="">Toutes entités</option>
              <option value="company">Membres</option>
              <option value="news">Actualités</option>
              <option value="event">Événements</option>
              <option value="prospect">Prospects</option>
              <option value="contact">Contacts</option>
            </select>
            <input value={filter.actor_email} onChange={(e) => setFilter({ ...filter, actor_email: e.target.value })} placeholder="email de l'auteur" className="flex-1 min-w-[180px] rounded-lg border border-neutral-200 px-3 py-1.5 text-sm" data-testid="filter-actor" />
          </div>
        </div>

        <div className="rounded-2xl border border-neutral-100 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-neutral-100 text-left text-xs uppercase text-neutral-500">
                <tr><th className="px-4 py-3">Date</th><th className="px-4 py-3">Acteur</th><th className="px-4 py-3">Action</th><th className="px-4 py-3">Entité</th><th className="px-4 py-3">Référence</th></tr>
              </thead>
              <tbody>
                {rows.length === 0 && <tr><td colSpan="5" className="py-8 text-center text-neutral-400">Aucune activité enregistrée.</td></tr>}
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-neutral-100" data-testid={`activity-row-${r.id}`}>
                    <td className="px-4 py-2.5 text-xs text-neutral-500 whitespace-nowrap">{r.created_at && new Date(r.created_at).toLocaleString("fr")}</td>
                    <td className="px-4 py-2.5 text-xs text-neutral-700"><User className="mr-1 inline h-3 w-3 text-neutral-400" />{r.actor_email || "—"} <span className="ml-1 text-neutral-400">({r.actor_role || "—"})</span></td>
                    <td className="px-4 py-2.5"><span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${ACTION_COLOR[r.action] || "bg-neutral-100 text-neutral-700"}`}>{r.action}</span></td>
                    <td className="px-4 py-2.5 text-xs text-neutral-600">{r.entity_type}</td>
                    <td className="px-4 py-2.5 text-xs text-neutral-700">{r.entity_label || r.entity_id || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </PortalLayout>
  );
}
